import { useState } from 'react';
import toast from 'react-hot-toast';
import {
  ArrowClockwiseIcon,
  FloppyDiskIcon,
  GitPullRequestIcon,
  SparkleIcon,
  WarningIcon,
} from '@phosphor-icons/react';

import { createPullRequest, type PullRequest, updatePullRequest } from '@/api-models/sandbox-git';
import { Alert } from '@/ui/Alert';
import { Button } from '@/ui/button/Button';
import { Spinner } from '@/ui/Spinner';
import { Textarea } from '@/ui/Textarea';

import { describePullRequestFailure, type PullRequestFailure } from './pull-request-error';
import { PullRequestLink } from './PullRequestLink';
import { StaffDetails } from './StaffDetails';

/**
 * A failed attempt, in full: headline, what to do about it, and the server's raw output.
 *
 * The raw output is the point. Every push failure reaches the client as `"Invalid request"` with
 * git's stderr in `details`, so a UI that renders only `error.message` — as this tab used to —
 * tells the user nothing about a rejected push, missing credentials or a protected branch.
 */
function PullRequestError({ failure, onRetry }: { failure: PullRequestFailure; onRetry: () => void }) {
  return (
    <Alert variant="danger" title={failure.message} icon={<WarningIcon size={14} />} className="p-3 text-xs">
      {failure.hint && <p className="text-fg-secondary">{failure.hint}</p>}
      <StaffDetails details={failure.details} />
      <div className="mt-2 flex items-center gap-3">
        <Button type="button" size={7} onClick={onRetry} iconLeft={<ArrowClockwiseIcon size={12} />}>
          Try again
        </Button>
        {failure.requestId && <span className="font-mono text-2xs text-fg-tertiary">{failure.requestId}</span>}
      </div>
    </Alert>
  );
}

interface PullRequestPanelProps {
  base: string;
  /** The current header title — the single source of truth for the PR title. */
  title: string;
  /** Description body, edited here. */
  body: string;
  onBodyChange: (value: string) => void;
  /** Why this branch cannot become a pull request, when it cannot; creation is disabled then. */
  blocker: string | null;
  /** The PR for this branch, whoever opened it — including the agent, from the sandbox. */
  pull: PullRequest | null;
  /** True until the lookup for an existing PR has answered. */
  loading: boolean;
  /** Provider reason the lookup failed: there may or may not already be a PR. */
  unknown: string | null;
  onSaved: (pull: PullRequest) => void;
  /** Fill title + body from the AI draft. */
  onGenerate: () => void;
  generating: boolean;
  generateError: string | null;
}

/**
 * Edit the PR body and open — or, once a PR exists, save — it. The title lives in the persistent
 * header above the tabs; this panel owns the body, the AI draft action, and the create/save button.
 *
 * Unlike the old read-only-after-open behaviour, an existing PR stays editable here: `update_pull`
 * is idempotent, so "Save changes" PATCHes the provider in place rather than sending the user away.
 */
export function PullRequestPanel({
  base,
  title,
  body,
  onBodyChange,
  blocker,
  pull,
  loading,
  unknown,
  onSaved,
  onGenerate,
  generating,
  generateError,
}: PullRequestPanelProps) {
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<PullRequestFailure | null>(null);

  // Withheld rather than defaulted to the form: offering "Create PR" for the split second before
  // the lookup answers is how the tab ends up inviting a duplicate of the agent's own PR.
  if (loading && !pull) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Spinner size={5} />
      </div>
    );
  }

  const trimmedTitle = title.trim();
  // The body is now seeded from the PR itself, so a real diff against it drives what we send.
  const bodyChanged = pull ? body !== (pull.body ?? '') : body.trim() !== '';

  const create = async () => {
    setPending(true);
    setFailure(null);
    try {
      onSaved(await createPullRequest(base, { title: trimmedTitle, body: body.trim() || undefined }));
    } catch (e) {
      setFailure(describePullRequestFailure(e));
    } finally {
      setPending(false);
    }
  };

  const save = async () => {
    if (!pull) return;
    setPending(true);
    setFailure(null);
    try {
      // Only send what changed.
      const patch: { number: number; title?: string; body?: string } = { number: pull.number };
      if (trimmedTitle && trimmedTitle !== pull.title) patch.title = trimmedTitle;
      if (bodyChanged) patch.body = body;
      onSaved(await updatePullRequest(base, patch));
      toast.success('Pull request updated');
    } catch (e) {
      setFailure(describePullRequestFailure(e));
    } finally {
      setPending(false);
    }
  };

  const hasEdits = pull ? (trimmedTitle !== '' && trimmedTitle !== pull.title) || bodyChanged : trimmedTitle !== '';

  return (
    <div className="flex flex-col gap-3 p-3">
      {blocker && !pull && (
        <Alert variant="warning" icon={<WarningIcon size={14} />} className="p-3 text-xs">
          {blocker}
        </Alert>
      )}
      {unknown && !pull && (
        // `null` from a failed lookup means "unknown", not "none" — so warn instead of silently
        // presenting the form as if the branch were definitely PR-less.
        <Alert variant="warning" icon={<WarningIcon size={14} />} className="p-3 text-xs">
          Could not check whether this branch already has a pull request.
          <StaffDetails details={unknown} />
        </Alert>
      )}

      <div className="flex items-center justify-between gap-2">
        <span className="text-2xs font-semibold tracking-wide text-fg-tertiary">DESCRIPTION</span>
        <Button
          type="button"
          variant="ghost"
          size={7}
          onClick={onGenerate}
          isLoading={generating}
          iconLeft={<SparkleIcon size={13} />}
        >
          {generating ? 'Generating…' : pull ? 'Regenerate description' : 'Generate with AI'}
        </Button>
      </div>
      <Textarea
        value={body}
        onChange={(e) => onBodyChange(e.target.value)}
        rows={12}
        placeholder={
          pull
            ? 'No description yet — write one or generate a draft with AI, then save.'
            : 'Describe the change, or generate a draft with AI (optional)'
        }
        className="resize-y text-xs"
      />

      {generateError && (
        <Alert variant="danger" icon={<WarningIcon size={14} />} className="p-3 text-xs">
          {generateError}
        </Alert>
      )}
      {failure && <PullRequestError failure={failure} onRetry={pull ? save : create} />}

      {pull ? (
        <div className="flex items-center gap-2">
          <Button
            type="button"
            onClick={save}
            isLoading={pending}
            disabled={!hasEdits || trimmedTitle.length === 0}
            iconLeft={<FloppyDiskIcon size={14} />}
          >
            {pending ? 'Saving…' : 'Save changes'}
          </Button>
          <PullRequestLink pull={pull} />
          <span className="text-2xs text-fg-tertiary">Edits the pull request on the provider.</span>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <Button
            type="button"
            onClick={create}
            isLoading={pending}
            disabled={blocker !== null || trimmedTitle.length === 0}
            iconLeft={<GitPullRequestIcon size={14} />}
          >
            {pending ? 'Opening…' : 'Create PR'}
          </Button>
          <span className="text-2xs text-fg-tertiary">Pushes the branch and opens a draft PR.</span>
        </div>
      )}
    </div>
  );
}
